/**
 * MenuVisibilityChecklist — lista de entradas del menú con casilla por entrada.
 *
 * Se usa en dos sitios, con la misma lógica de visibilidad:
 *   - MenuCustomizer (el propio usuario elige qué oculta en la empresa activa).
 *   - PlatformAdmin (el superadmin decide qué oculta una empresa a sus usuarios).
 *
 * `hiddenKeys` es la selección que se está editando; `lockedKeys` es lo que la
 * política de empresa oculta y nadie puede des-ocultar (sale marcado como
 * bloqueado y no responde al clic).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import './MenuVisibilityChecklist.css';

function MenuVisibilityChecklist({
  groups,
  hiddenKeys,
  lockedKeys = [],
  onToggleItem,
  onToggleGroup,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const hidden = hiddenKeys instanceof Set ? hiddenKeys : new Set(hiddenKeys || []);
  const locked = lockedKeys instanceof Set ? lockedKeys : new Set(lockedKeys || []);

  const editableItems = (group) => group.items.filter((item) => !locked.has(item.key));
  const isVisible = (key) => !hidden.has(key) && !locked.has(key);
  const groupState = (group) => {
    const items = editableItems(group);
    if (!items.length) return 'locked';
    const visibleCount = items.filter((item) => isVisible(item.key)).length;
    if (visibleCount === 0) return 'none';
    if (visibleCount === items.length) return 'all';
    return 'some';
  };

  return (
    <div className="mvc-root">
      {groups.map((group) => {
        const state = groupState(group);
        return (
          <section key={group.key} className="mvc-group">
            <header className="mvc-group-header">
              <span className="mvc-group-dot" style={{ background: group.color }} />
              <h4 className="mvc-group-title">{t(`menu.groups.${group.key}`, group.label)}</h4>
              <span className="mvc-group-count">
                {group.items.filter((item) => isVisible(item.key)).length}/{group.items.length}
              </span>
              {!readOnly && state !== 'locked' && (
                <button
                  type="button"
                  className="mvc-group-toggle"
                  onClick={() => onToggleGroup?.(group, state === 'all' ? [] : editableItems(group).map((i) => i.key))}
                >
                  {state === 'all'
                    ? t('menu.customize.hideAll', 'Ocultar todo')
                    : t('menu.customize.showAll', 'Mostrar todo')}
                </button>
              )}
            </header>

            <div className="mvc-items">
              {group.items.map((item) => {
                const isLocked = locked.has(item.key);
                const visible = isVisible(item.key);
                return (
                  <label
                    key={item.key}
                    className={`mvc-item ${isLocked ? 'mvc-item--locked' : ''} ${readOnly ? 'mvc-item--readonly' : ''}`}
                    title={
                      isLocked
                        ? t('menu.customize.companyLocked', 'Oculto por la política de la empresa')
                        : t(`menu.items.${item.key}`, item.label)
                    }
                  >
                    <input
                      type="checkbox"
                      checked={visible}
                      disabled={isLocked || readOnly}
                      onChange={() => onToggleItem?.(item.key)}
                    />
                    <span className="mvc-item-label">{t(`menu.items.${item.key}`, item.label)}</span>
                    {isLocked && (
                      <span className="mvc-item-lock">
                        <LockClosedIcon />
                        {t('company.current', 'Empresa')}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default MenuVisibilityChecklist;
